import {
  Badge,
  Button,
  Checkbox,
  Input,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@devhop/ui";
import {
  RiArrowDownSLine,
  RiArrowRightSLine,
  RiCheckLine,
  RiDeleteBinLine,
  RiSettings3Line,
} from "@remixicon/react";
import { useId, useRef, useState } from "react";
import {
  isValidCfCode,
  normalizeCfCode,
} from "@/modules/cf-feed/domain/lib/cf-code";
import type {
  FeedSessionProductGroup,
  FeedSessionProductRow,
} from "@/modules/cf-feed/presentation/api/types";
import type { ProductAllocationInput } from "@/modules/cf-feed/presentation/lib/product-allocation";
import { useBoundedInfiniteScroll } from "@/modules/cf-feed/presentation/lib/use-bounded-infinite-scroll";
import { PreorderProductMeta } from "@/modules/cf-feed/presentation/ui/PreorderProductMeta";
import { SessionProductAllocationDialog } from "@/modules/cf-feed/presentation/ui/SessionProductAllocationDialog";
import { SessionProductQuota } from "@/modules/cf-feed/presentation/ui/SessionProductQuota";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { formatKip, formatPriceInput, Thumb } from "./table-helpers";

/** ลายเซ็นชุด option ที่ผูกแล้ว — ใช้เทียบ bulk */
export function sessionProductOptionSignature(
  options: Array<{ adminOptionDefinitionId: string }>,
): string {
  return options
    .map((o) => o.adminOptionDefinitionId)
    .slice()
    .sort()
    .join("|");
}

export type FeedProductsTableProps = {
  products: FeedSessionProductGroup[];
  isLoading?: boolean;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore?: () => void;
  /** session product ids ที่เลือก (select ที่ชั้นสินค้า = ทุก variant ภายใต้) */
  selectedIds: Set<string>;
  onSelectionChange: (selectedIds: Set<string>) => void;
  onBulkEditOptions?: (ids: string[]) => void;
  onBulkRemove?: (ids: string[]) => void;
  onToggleSale: (id: string, enabled: boolean) => void;
  onSaveCfCode?: (id: string, cfCode: string) => Promise<unknown>;
  onSaveAllocation?: (
    id: string,
    input: ProductAllocationInput,
  ) => Promise<unknown>;
  onEditOptions?: (id: string, title: string) => void;
  onRemove: (id: string) => void;
  isSaving: boolean;
  isRemoving: boolean;
};

function productVariantIds(product: FeedSessionProductGroup): string[] {
  return product.variants.map((v) => v.id);
}

function isProductFullySelected(
  product: FeedSessionProductGroup,
  selectedIds: Set<string>,
): boolean {
  const ids = productVariantIds(product);
  return ids.length > 0 && ids.every((id) => selectedIds.has(id));
}

function isProductPartiallySelected(
  product: FeedSessionProductGroup,
  selectedIds: Set<string>,
): boolean {
  const ids = productVariantIds(product);
  const n = ids.filter((id) => selectedIds.has(id)).length;
  return n > 0 && n < ids.length;
}

function CentralStockCell({ p }: { p: FeedSessionProductRow }) {
  const available = p.availableQuantity;
  return (
    <div>
      <p className="font-medium tabular-nums">
        {available == null ? "ຮັບ preorder" : `CF ໄດ້ ${available} ໂຕ`}
      </p>
      <p className="text-muted-foreground text-xs">
        ຄັງ {p.stockOnHand} · ສັ່ງແລ້ວ {p.soldQuantity}
      </p>
    </div>
  );
}

function SessionProductCfCodeEditor({
  product,
  label,
  isSaving,
  onSave,
}: {
  product: FeedSessionProductRow;
  label: string;
  isSaving: boolean;
  onSave: NonNullable<FeedProductsTableProps["onSaveCfCode"]>;
}) {
  const hintId = useId();
  const [draft, setDraft] = useState<string>();
  const [pending, setPending] = useState(false);
  const savingRef = useRef(false);
  const value = draft ?? product.cfCode;
  const normalized = normalizeCfCode(value);
  const dirty = draft != null && normalized !== product.cfCode;
  const invalid = draft != null && !isValidCfCode(normalized);
  const disabled = isSaving || pending;
  const canSave = dirty && isValidCfCode(normalized) && !disabled;

  const save = async () => {
    if (!canSave || savingRef.current) return;
    savingRef.current = true;
    setPending(true);
    try {
      await onSave(product.id, normalized);
      setDraft(undefined);
    } catch {
      // mutation แสดงข้อผิดพลาดแล้ว — เก็บรหัสที่พิมพ์ไว้ให้แก้ต่อได้
    } finally {
      savingRef.current = false;
      setPending(false);
    }
  };

  return (
    <div className="min-w-36">
      <div className="flex items-center gap-1">
        <Input
          aria-label={`ລະຫັດ CF ${label}`}
          aria-invalid={invalid}
          aria-describedby={invalid ? hintId : undefined}
          className="h-8 w-28 font-mono text-xs uppercase"
          maxLength={10}
          disabled={disabled}
          value={value}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={() => {
            if (draft != null) setDraft(normalized);
          }}
          onKeyDown={(event) => {
            if (event.nativeEvent.isComposing) return;
            if (event.key === "Enter") {
              event.preventDefault();
              void save();
            }
            if (event.key === "Escape" && !disabled) setDraft(undefined);
          }}
        />
        <Button
          type="button"
          size="icon"
          variant={dirty ? "default" : "ghost"}
          className="size-8 shrink-0"
          disabled={!canSave}
          aria-label={`ບັນທຶກລະຫັດ CF ${label}`}
          title="ບັນທຶກລະຫັດ CF"
          onClick={() => void save()}
        >
          <RiCheckLine className="size-4" aria-hidden />
        </Button>
      </div>
      {invalid ? (
        <p
          id={hintId}
          className="mt-1 max-w-40 whitespace-normal text-destructive text-xs"
        >
          ໃຊ້ A–Z, 0–9 ຫຼື - ຈຳນວນ 1–10 ຕົວ
        </p>
      ) : null}
    </div>
  );
}

export function FeedProductsTable({
  products,
  isLoading,
  hasNextPage = false,
  isFetchingNextPage = false,
  onLoadMore,
  selectedIds,
  onSelectionChange,
  onBulkEditOptions,
  onBulkRemove,
  onToggleSale,
  onSaveCfCode,
  onSaveAllocation,
  onEditOptions,
  onRemove,
  isSaving,
  isRemoving,
}: FeedProductsTableProps) {
  const { scrollRootRef, sentinelRef } = useBoundedInfiniteScroll({
    hasNextPage,
    isFetchingNextPage,
    onLoadMore,
  });
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(
    () => new Set(),
  );
  const [allocationTargetId, setAllocationTargetId] = useState<string | null>(
    null,
  );
  const allocationTarget = allocationTargetId
    ? products
        .flatMap((product) => product.variants)
        .find((variant) => variant.id === allocationTargetId)
    : undefined;

  const allVariantIds = products.flatMap((p) => productVariantIds(p));
  const allChecked =
    allVariantIds.length > 0 &&
    allVariantIds.every((id) => selectedIds.has(id));
  const someChecked = allVariantIds.some((id) => selectedIds.has(id));
  const selectedProductCount = products.filter((p) =>
    isProductFullySelected(p, selectedIds),
  ).length;
  const selectedSessionCount = selectedIds.size;

  return (
    <div className="space-y-3">
      {selectedProductCount > 0 ? (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-muted/30 px-3 py-2">
          <p className="text-sm">
            ເລືອກແລ້ວ{" "}
            <span className="font-semibold">{selectedProductCount}</span> ສິນຄ້າ
            <span className="text-muted-foreground">
              {" "}
              ({selectedSessionCount} ຕົວເລືອກ)
            </span>
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => onSelectionChange(new Set())}
            >
              ຍົກເລີກການເລືອກ
            </Button>
            {onBulkEditOptions ? (
              <Button
                type="button"
                size="sm"
                disabled={selectedSessionCount < 2}
                onClick={() => onBulkEditOptions([...selectedIds])}
              >
                ຕັ້ງ option ຫຼາຍລາຍການ
              </Button>
            ) : null}
            {onBulkRemove ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="text-destructive"
                disabled={isRemoving}
                onClick={() => onBulkRemove([...selectedIds])}
              >
                ລຶບທີ່ເລືອກ
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}

      <div
        ref={scrollRootRef}
        className="max-h-[36rem] overflow-auto overscroll-contain rounded-md border"
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                {allVariantIds.length > 0 ? (
                  <Checkbox
                    aria-label="ເລືອກສິນຄ້າທັງໝົດ"
                    checked={
                      allChecked ? true : someChecked ? "indeterminate" : false
                    }
                    onCheckedChange={(value) => {
                      const next = new Set(selectedIds);
                      if (value) {
                        for (const id of allVariantIds) next.add(id);
                      } else {
                        for (const id of allVariantIds) next.delete(id);
                      }
                      onSelectionChange(next);
                    }}
                  />
                ) : null}
              </TableHead>
              <TableHead>ສິນຄ້າ / ຕົວເລືອກ</TableHead>
              <TableHead>CF</TableHead>
              <TableHead>ລາຄາ</TableHead>
              <TableHead>ສະຕັອກກາງ</TableHead>
              <TableHead>ເປີດຂາຍ</TableHead>
              <TableHead>ເວລາ</TableHead>
              <TableHead>ຈັດການ</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && products.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="h-28 text-center">
                  ກຳລັງໂຫຼດສິນຄ້າ...
                </TableCell>
              </TableRow>
            ) : products.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={8}
                  className="h-28 text-center text-muted-foreground"
                >
                  ຍັງບໍ່ມີສິນຄ້າໃນໂພສ
                </TableCell>
              </TableRow>
            ) : (
              products.flatMap((product) => {
                const productOpen = expandedProducts.has(product.productId);
                const limitedCount = product.variants.filter(
                  (variant) =>
                    variant.allocationMode === "limit" || !variant.isPreorder,
                ).length;
                const unlimitedCount = product.variants.length - limitedCount;
                const fullySelected = isProductFullySelected(
                  product,
                  selectedIds,
                );
                const partiallySelected = isProductPartiallySelected(
                  product,
                  selectedIds,
                );
                const productRow = (
                  <TableRow key={`product-${product.productId}`}>
                    <TableCell>
                      <Checkbox
                        aria-label={`ເລືອກ ${product.title}`}
                        checked={
                          fullySelected
                            ? true
                            : partiallySelected
                              ? "indeterminate"
                              : false
                        }
                        onCheckedChange={(value) => {
                          const next = new Set(selectedIds);
                          const ids = productVariantIds(product);
                          if (value) {
                            for (const id of ids) next.add(id);
                          } else {
                            for (const id of ids) next.delete(id);
                          }
                          onSelectionChange(next);
                        }}
                      />
                    </TableCell>
                    <TableCell colSpan={7}>
                      <Button
                        type="button"
                        variant="ghost"
                        className="h-auto w-full justify-start gap-2 p-0 text-left"
                        aria-expanded={productOpen}
                        onClick={() =>
                          setExpandedProducts((current) => {
                            const next = new Set(current);
                            if (next.has(product.productId)) {
                              next.delete(product.productId);
                            } else {
                              next.add(product.productId);
                            }
                            return next;
                          })
                        }
                      >
                        {productOpen ? (
                          <RiArrowDownSLine className="size-4 shrink-0" />
                        ) : (
                          <RiArrowRightSLine className="size-4 shrink-0" />
                        )}
                        <Thumb src={product.imageUrl} />
                        <span className="min-w-0">
                          <span className="block truncate font-medium">
                            {product.title}
                          </span>
                          <span className="block text-muted-foreground text-xs">
                            {product.variants.length} ຕົວເລືອກ
                          </span>
                          <span className="flex flex-wrap gap-x-2 text-muted-foreground text-xs">
                            {limitedCount > 0 ? (
                              <span>CF ຈຳກັດ {limitedCount}</span>
                            ) : null}
                            {unlimitedCount > 0 ? (
                              <span>CF ບໍ່ຈຳກັດ {unlimitedCount}</span>
                            ) : null}
                          </span>
                        </span>
                      </Button>
                    </TableCell>
                  </TableRow>
                );

                if (!productOpen) return [productRow];

                const variantRows = product.variants.map((p) => {
                  const options = p.options ?? [];
                  const hasOverride =
                    p.discountPercent != null || p.discountFixedLak != null;
                  const label = p.variantLabel?.trim() || p.cfCode || p.title;
                  return (
                    <TableRow key={`variant-${p.id}`} className="bg-muted/20">
                      <TableCell />
                      <TableCell>
                        <div className="space-y-1.5 pl-8">
                          <p className="font-medium text-sm">{label}</p>
                          <SessionProductQuota product={p} />
                          {onSaveAllocation ? (
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs"
                              disabled={isSaving || isRemoving}
                              aria-label={`ແກ້ໄຂໂໝດ CF ${label}`}
                              onClick={() => setAllocationTargetId(p.id)}
                            >
                              ແກ້ໄຂໂໝດ CF
                            </Button>
                          ) : null}
                          {p.isPreorder ? (
                            <PreorderProductMeta
                              preorderExpectedDate={p.preorderExpectedDate}
                              preorderEndAt={p.preorderEndAt}
                              showEndHint
                            />
                          ) : null}
                          {options.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {options.map((opt) => (
                                <Badge
                                  key={`${p.id}-${opt.adminOptionDefinitionId}`}
                                  variant="secondary"
                                  className="font-normal tabular-nums"
                                >
                                  {opt.surchargeLak === 0
                                    ? `${opt.label} ຟຣີ`
                                    : `${opt.label} +${formatPriceInput(opt.surchargeLak)}`}
                                </Badge>
                              ))}
                            </div>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell>
                        {onSaveCfCode ? (
                          <SessionProductCfCodeEditor
                            product={p}
                            label={label}
                            isSaving={isSaving}
                            onSave={onSaveCfCode}
                          />
                        ) : (
                          <span className="font-mono text-xs">{p.cfCode}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="tabular-nums">{formatKip(p.price)}</p>
                          {hasOverride ? (
                            <p className="text-muted-foreground text-xs">
                              ສ່ວນຫຼຸດ
                              {p.discountPercent != null
                                ? ` ${p.discountPercent}%`
                                : ""}
                              {p.discountFixedLak != null
                                ? ` -${formatKip(p.discountFixedLak)}`
                                : ""}
                            </p>
                          ) : (
                            <p className="text-muted-foreground text-xs">
                              ໃຊ້ສ່ວນຫຼຸດລາຍການ
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <CentralStockCell p={p} />
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={p.saleEnabled}
                          onCheckedChange={(v) =>
                            onToggleSale(p.id, v === true)
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <span className="text-muted-foreground text-xs">
                          {formatDateTimeLocal(p.addedAt)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {onEditOptions ? (
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              disabled={isSaving}
                              onClick={() =>
                                onEditOptions(
                                  p.id,
                                  p.variantLabel?.trim() || p.title,
                                )
                              }
                              title="ຕັ້ງຄ່າ option"
                            >
                              <RiSettings3Line className="size-4" />
                            </Button>
                          ) : null}
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="text-destructive"
                            disabled={isRemoving}
                            onClick={() => onRemove(p.id)}
                            title="ລຶບອອກຈາກໂພສ"
                          >
                            <RiDeleteBinLine className="size-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                });

                return [productRow, ...variantRows];
              })
            )}
          </TableBody>
        </Table>
        {hasNextPage ? (
          <div
            ref={sentinelRef}
            className="flex min-h-10 items-center justify-center text-muted-foreground text-xs"
            aria-live="polite"
          >
            {isFetchingNextPage ? "ກຳລັງໂຫຼດເພີ່ມ..." : "ເລື່ອນເພື່ອໂຫຼດເພີ່ມ"}
          </div>
        ) : null}
      </div>
      {allocationTarget && onSaveAllocation ? (
        <SessionProductAllocationDialog
          key={allocationTarget.id}
          product={allocationTarget}
          isSaving={isSaving}
          onSave={onSaveAllocation}
          onClose={() => setAllocationTargetId(null)}
        />
      ) : null}
    </div>
  );
}
